import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination, Navigation, Autoplay } from "swiper";
import config from "../../../configuration/config";
import controllersApi from "../../../services/controllersApi";
import ProtectionServiceCard from "./ProtectionServiceCard";
import { getData, User_Data_Key } from "../../../utils/localStorageManager";
import Skeleton from 'react-loading-skeleton';
import { useHistory } from "react-router-dom";
import { DropDownInput } from "../../../components/_input/_dropdown";

// install Swiper modules
SwiperCore.use([Pagination, Navigation, Autoplay]);

const ProtectionAndShadingHomeSection = () => {
    const { t: trans, i18n } = useTranslation(["info", "eparts", "global", "model"]);
    const lang = i18n.language || "ar";
    const dir = lang === "ar" ? "rtl" : "ltr";
    const history = useHistory();

    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLogin, setLogin] = useState(false);
    const [vehicales, setVehicales] = useState([]);
    const [activeVehicleTab, setActiveVehicleTab] = useState('myCars');

    // Selection state
    const [selectedMyCar, setSelectedMyCar] = useState(null);

    // New 3-dropdown states
    const [brands, setBrands] = useState([]);
    const [groups, setGroups] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [fGroups, setFGroups] = useState([]);
    const [fSubCategories, setFSubCategories] = useState([]);

    const [modelSearch, setModelSearch] = useState({
        brandID: "",
        groupID: "",
        subCategoryID: "",
        vehicleType: ""
    });

    // Initial Auth & Data
    useEffect(() => {
        const isAuth = !!localStorage.getItem("isAuthenticated");
        setLogin(isAuth);
        if (isAuth) {
            getVehicales();
        } else {
            setActiveVehicleTab('newCar');
            setLoading(false);
        }
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getVehicales = async () => {
        try {
            const cleanData = JSON.parse(getData(User_Data_Key, true));
            const _userID = cleanData?.id?.toString().split('_')[1] || cleanData?.userId || cleanData?.UserID;

            if (!_userID) {
                setLoading(false);
                return;
            }

            const response = await controllersApi.post('api/Pages/SiteGetUserCars', { "userID": Number(_userID) });
            if (response.data && Array.isArray(response.data)) {
                setVehicales(response.data);
                if (response.data.length > 0) {
                    setSelectedMyCar(response.data[0]);
                    const vType = response.data[0].CarCategory || response.data[0].carCategory;
                    fetchServices(vType);
                } else {
                    setActiveVehicleTab('newCar');
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching user cars:", error);
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await axios.request({
                baseURL: config.controllURL,
                url: lang + "/api/Maintenance/Settings",
                method: "get",
            });
            const result = response.data;
            if (result) {
                const customizedBrands = (result?.brands || []).map((row) => ({
                    value: row.BrandID || row.brandID,
                    text: lang === 'ar' ? (row.DescriptionAr || row.description || row.Description) : (row.DescriptionEn || row.description || row.Description),
                }));
                setBrands(customizedBrands || []);
                setGroups(result?.groups || []);
                setSubCategories(result?.productModels || []);
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
        }
    };

    const handleFilterGroups = (brandID) => {
        if (groups) {
            const filteredValues = groups.filter((e) => {
                const year = parseInt(e.Year || e.year || 0);
                return (e.BrandId == brandID || e.brandID == brandID) &&
                    !(e.Description || e.description)?.toLowerCase().includes("test") &&
                    year >= 2025;
            });

            // Group by description and keep only the entry with the highest year
            const latestGroups = Object.values(
                filteredValues.reduce((acc, current) => {
                    const desc = (current.Description || current.description)?.trim();
                    const year = current.Year || current.year || 0;
                    if (!acc[desc] || parseInt(year) > parseInt(acc[desc].Year || acc[desc].year || 0)) {
                        acc[desc] = current;
                    }
                    return acc;
                }, {})
            );

            setFGroups(
                latestGroups.map((e) => {
                    const desc = e.Description || e.description;
                    const year = e.Year || e.year;
                    const groupID = e.ProductGroupId || e.productGroupID;
                    return {
                        value: groupID,
                        text: `${desc} ${year}`,
                        label: (
                            <Fragment>
                                {desc} <span style={{ color: '#18a94e', marginInlineStart: '8px' }}>{year}</span>
                            </Fragment>
                        )
                    };
                })
            );
        }
    };

    const handleFilterSubCategories = (groupID) => {
        if (subCategories) {
            console.log("ProtectionSection: Filtering subcategories for groupID:", groupID);
            const filteredValues = subCategories.filter((e) => {
                const eGroupID = e.ProductGroupId || e.productGroupID || e.GroupID || e.groupID;
                return String(eGroupID) === String(groupID);
            });
            const mapped = filteredValues.map((e) => {
                const vType = e.CarCategory || e.carCategory || e.CarCategory || e.carCategory || e.CarCategory || e.CarCategory || e.modelType || e.ModelType || "";
                if (!vType || vType === "") {
                    console.warn("ProtectionSection: Item missing VehicleType. Raw keys:", Object.keys(e), "Raw object:", e);
                }
                return {
                    value: e.ProductTypeId || e.productTypeID || e.ProductID || e.id || "",
                    text: lang === 'ar' ? (e.descriptionAr || e.DescriptionAr || e.description || e.description_ar || "") : (e.descriptionEn || e.DescriptionEn || e.description || e.ShortDescriptionEn || e.description_en || ""),
                    modelCode: e.ModelCode || e.modelCode || "",
                    vehicleType: vType
                };
            });
            console.log("ProtectionSection: fSubCategories mapped array:", mapped);
            setFSubCategories(mapped);
        }
    };

    const fetchServices = async (vType) => {
        if (!vType) {
            console.log("ProtectionSection: fetchServices called with empty vType");
            return;
        }
        try {
            setLoading(true);
            console.log("ProtectionSection: Fetching services for VehicleType:", vType);
            const response = await controllersApi.post('api/Pages/site_FetchProtectionServiceByVehicleType_forNewCar', { "VehicleType": String(vType).trim() });

            const result = response.data;
            console.log("ProtectionSection: Raw API Result:", result);

            // Check multiple possible locations for the data array
            const dataArray = Array.isArray(result) ? result :
                (Array.isArray(result?.data) ? result.data :
                    (Array.isArray(result?.values) ? result.values :
                        (Array.isArray(result?.ApiObjectData) ? result.ApiObjectData :
                            (Array.isArray(result?.result) ? result.result : []))));

            console.log("ProtectionSection: Extracted dataArray:", dataArray);

            if (dataArray.length > 0) {
                const normalizedData = dataArray.map(s => ({
                    ...s,
                    ServiceName: s.ServiceName || s.serviceName || '',
                    ServiceNameAR: s.ServiceNameAR || s.serviceNameAR || s.serviceNameAr || '',
                    Price: s.Price || s.price || 0,
                    ServiceID: s.ServiceID || s.serviceID || s.id || 0
                }));

                const validData = normalizedData.filter(s => {
                    const nEn = (s.ServiceName || '').toLowerCase();
                    const nAr = (s.ServiceNameAR || '').toLowerCase();
                    return !nEn.includes('test') && !nAr.includes('test') && !nAr.includes('تيست');
                });

                const getPriorityLevel = (s) => {
                    const nameAr = s.ServiceNameAR.toLowerCase();
                    const nameEn = s.ServiceName.toLowerCase();

                    if (nameEn.includes('diamond') || nameAr.includes('ماسية') || nameAr.includes('الماسيه')) return 4;
                    if (nameEn.includes('platinum') || nameAr.includes('بلاتينية') || nameAr.includes('البلاتينيه')) return 3;
                    if (nameEn.includes('gold') || nameAr.includes('ذهبية') || nameAr.includes('الذهبيه')) return 2;
                    if (nameEn.includes('silver') || nameAr.includes('فضية') || nameAr.includes('الفضيه')) return 1;
                    return 0;
                };

                const special = validData.filter(s => getPriorityLevel(s) > 0);
                const rest = validData.filter(s => getPriorityLevel(s) === 0);

                special.sort((a, b) => (a.Price || 0) - (b.Price || 0));
                rest.sort((a, b) => (a.Price || 0) - (b.Price || 0));

                setServices([...special, ...rest]);
            } else {
                setServices([]);
            }
        } catch (error) {
            console.error("Error fetching services:", error);
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (service) => {
        const vehicleInfo = activeVehicleTab === 'myCars' ? {
            isNewCar: false,
            car: selectedMyCar,
            vin: selectedMyCar?.VIN || selectedMyCar?.CarID || selectedMyCar?.SN || "0",
            vehicleType: selectedMyCar?.VehicleType
        } : {
            isNewCar: true,
            brand: modelSearch.brandID,
            year: null,
            type: modelSearch.subCategoryID,
            vin: "0",
            vehicleType: modelSearch.vehicleType
        };

        history.push({
            pathname: `/${lang}/protection-service/${service.ServiceID}`,
            state: { serviceData: service, vehicleInfo }
        });
    };

    return (
        <section className="prot-sec" id="protection-section">
            <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
                    <div>
                        <div className="prot-stag">{trans("info:protection_and_shading")}</div>
                        <h2 className="prot-sh">
                            {lang === 'ar' ? 'العناية و' : 'Car Care &'} <span className="gn">{lang === 'ar' ? 'الحماية' : 'Protection'}</span>
                        </h2>
                    </div>
                    <div className="prot-swiper-navs">
                        <button className="prot-swiper-btn prot-prev">
                            {lang === 'ar' ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            )}
                        </button>
                        <button className="prot-swiper-btn prot-next">
                            {lang === 'ar' ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            )}
                        </button>
                    </div>
                </div>

                <div className="prot-search-card relative">
                    {/* Background effects wrapped in overflow-hidden to prevent spilling while allowing dropdowns to spill */}
                    <div className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                    </div>

                    <div className="prot-vtabs relative z-10">
                        {isLogin && (
                            <button
                                className={`prot-vtab ${activeVehicleTab === 'myCars' ? 'on' : ''}`}
                                onClick={() => setActiveVehicleTab('myCars')}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H4a2 2 0 0 0-2 2v7.55a1 1 0 0 0 .84.99L5 17v-1h4v1h4v-1h2v1h4zM7 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" /></svg>
                                {trans("info:reserve_for_my_vehicle")}
                            </button>
                        )}
                        <button
                            className={`prot-vtab ${activeVehicleTab === 'newCar' ? 'on' : ''}`}
                            onClick={() => setActiveVehicleTab('newCar')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                            {trans("info:reserve_for_another_vehicle")}
                        </button>
                    </div>

                    <div className="prot-search-row relative z-10">
                        {activeVehicleTab === 'myCars' ? (
                            <div className="col-span-full" style={{ gridColumn: '1 / -1' }}>
                                <DropDownInput
                                    title={trans("info:select_car")}
                                    options={vehicales.map(v => ({
                                        text: `${lang === 'ar' ? v.BrandDescriptionAr : v.BrandDescriptionEn} ${lang === 'ar' ? v.ModelDescriptionAr : v.ModelDescriptionEn} (${v.Model}) - ${v.VIN || v.CarID}`,
                                        value: v.VIN || v.CarID
                                    }))}
                                    selectedValue={selectedMyCar?.VIN || selectedMyCar?.CarID}
                                    onfilter={(opt) => {
                                        const v = vehicales.find(x => (x.VIN || x.CarID) === opt.value);
                                        setSelectedMyCar(v);
                                        fetchServices(v?.VehicleType);
                                    }}
                                    isepart={true}
                                />
                            </div>
                        ) : (
                            <>
                                <DropDownInput
                                    title={trans("info:brand")}
                                    placeholder={trans("info:company")}
                                    options={brands}
                                    selectedValue={modelSearch.brandID}
                                    onSelect={(val) => {
                                        setModelSearch(prev => ({ ...prev, brandID: val, groupID: "", subCategoryID: "" }));
                                        setFGroups([]);
                                        setFSubCategories([]);
                                        if (val) handleFilterGroups(val);
                                    }}
                                    isepart={true}
                                />
                                <DropDownInput
                                    title={trans("info:group")}
                                    placeholder={trans("eparts:vehicle")}
                                    options={fGroups}
                                    selectedValue={modelSearch.groupID}
                                    disabled={!modelSearch.brandID}
                                    onSelect={(val) => {
                                        setModelSearch(prev => ({ ...prev, groupID: val, subCategoryID: "" }));
                                        setFSubCategories([]);
                                        if (val) handleFilterSubCategories(val);
                                    }}
                                    isepart={true}
                                />
                                <DropDownInput
                                    title={trans("info:model")}
                                    placeholder={trans("eparts:model")}
                                    options={fSubCategories}
                                    selectedValue={modelSearch.subCategoryID}
                                    disabled={!modelSearch.groupID}
                                    onSelect={(val) => {
                                        console.log("ProtectionSection: Model selected value:", val);
                                        const selectedModel = fSubCategories.find(m => String(m.value) === String(val));
                                        console.log("ProtectionSection: Found model object:", selectedModel);
                                        if (selectedModel) {
                                            fetchServices(selectedModel.vehicleType);
                                            setModelSearch(prev => ({
                                                ...prev,
                                                subCategoryID: val,
                                                vehicleType: selectedModel.vehicleType
                                            }));
                                        } else {
                                            console.warn("ProtectionSection: Selected model value not found in fSubCategories array");
                                        }
                                    }}
                                    isepart={true}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="relative">
                    {loading ? (
                        <div className="prot-grid">
                            {[1, 2, 3, 4].map(i => <div key={i} className="h-[350px]"><Skeleton height="100%" borderRadius={18} /></div>)}
                        </div>
                    ) : (
                        <>
                            <Swiper
                                dir={dir}
                                slidesPerView={1.25}
                                spaceBetween={12}
                                navigation={{ prevEl: '.prot-prev', nextEl: '.prot-next' }}
                                breakpoints={{
                                    640: { slidesPerView: 2.5 },
                                    1024: { slidesPerView: 3.6 },
                                    1366: { slidesPerView: 4.6 }
                                }}
                                className="pb-16"
                            >
                                {services.map((service, index) => {
                                    const serviceName = lang === 'ar' ? service.ServiceNameAR : service.ServiceName;
                                    const isPriority = ['gold', 'silver', 'diamond', 'platinum', 'ذهبية', 'فضية', 'ماسية', 'بلاتينية'].some(p => (serviceName || '').toLowerCase().includes(p));
                                    return (
                                        <SwiperSlide key={service.ServiceID || index} style={{ paddingTop: '10px' }}>
                                            <ProtectionServiceCard
                                                service={service}
                                                lang={lang}
                                                isPackage={isPriority}
                                                packageType={isPriority ? serviceName : null}
                                                onClick={() => handleCardClick(service)}
                                            />
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                            {services.length === 0 && (
                                <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/20 rounded-[18px] border border-gray-200 dark:border-gray-800">
                                    <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H4a2 2 0 0 0-2 2v7.55a1 1 0 0 0 .84.99L5 17v-1h4v1h4v-1h2v1h4zM7 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" /></svg>
                                    </div>
                                    <h3 className="text-18 font-bold text-gray-500">{trans("info:no_offer")}</h3>
                                    <p className="text-14 text-gray-400 mt-2">{trans("info:select_car")}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ProtectionAndShadingHomeSection;
